import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { useEffect, ReactNode } from 'react'

import { endRequest, receiveErrorAlerts, startRequest } from '@/actions'
import config from '@/config'
import { ERROR_MESSAGE_UNKNOWN } from '@/constants'
import { SilentCustomRequestError } from '@/helpers/requestErrors'
import { sendApiErrorsToSentry } from '@/helpers/sentry'
import { useAppDispatch } from '@/store'
import { ENDPOINTS } from '@/store/endpoints'

import { useResetSessionAndLogout } from './auth/hooks/use-reset-session-and-logout'

import { getAccessToken } from './auth/get-access-token'

class MissingTokenError extends Error {
    name = 'MissingTokenError'
}

const getShouldSendErrorToSentry = (axiosError: AxiosError) => {
    const status = axiosError?.response?.status

    // Log all 400 & 5XX response errors to Sentry
    return status === 400 || String(status)[0] === '5'
}

const getIsUnauthorizedRequest = (error: AxiosError | MissingTokenError) => {
    if (error instanceof MissingTokenError) return true

    const status = error?.response?.status
    const url = error?.config?.url

    // @TODO: Figure out if we even need to handle `loggedin` endpoint any differently
    return status === 401 && url !== ENDPOINTS.PROFILE.AUTHENTICATE
}

const getIsPublicEndpoint = (requestConfig: AxiosRequestConfig) => {
    const isMagicLinkEndpoint = !!requestConfig?.url?.includes('magiclinks/') && requestConfig?.method === 'get'
    return isMagicLinkEndpoint
}

let requestInterceptorInstance: number
let responseInterceptorInstance: number

export const InterceptorProvider = ({ children }: { children: ReactNode }) => {
    const dispatch = useAppDispatch()

    const { resetSessionAndLogout } = useResetSessionAndLogout()

    useEffect(() => {
        // Cleans up previous request interceptor on re-render
        if (requestInterceptorInstance !== undefined) axios.interceptors.request.eject(requestInterceptorInstance)

        requestInterceptorInstance = axios.interceptors.request.use(async (requestConfig) => {
            dispatch(startRequest())

            if (requestConfig.baseURL === config.apiUrl) {
                if (getIsPublicEndpoint(requestConfig)) return requestConfig

                const token = await getAccessToken()

                if (!token) {
                    // @TODO: Surfacing for testing, later maybe worth sending to Sentry
                    dispatch(receiveErrorAlerts('MissingTokenError'))

                    resetSessionAndLogout()

                    throw new MissingTokenError()
                }

                const requestConfigWithToken = { ...requestConfig }
                requestConfigWithToken.headers.Authorization = `Bearer ${token}`

                return requestConfigWithToken
            }

            return requestConfig
        })
    }, [dispatch, resetSessionAndLogout])

    useEffect(() => {
        // Cleans up previous response interceptor on re-render
        if (responseInterceptorInstance !== undefined) axios.interceptors.response.eject(responseInterceptorInstance)

        responseInterceptorInstance = axios.interceptors.response.use(
            (response) => {
                dispatch(endRequest())
                return response
            },
            (error: AxiosError) => {
                dispatch(endRequest())

                if (getShouldSendErrorToSentry(error)) {
                    sendApiErrorsToSentry(error)
                }

                if (getIsUnauthorizedRequest(error)) {
                    // @TODO: Surfacing for testing, later maybe worth sending to Sentry
                    dispatch(receiveErrorAlerts('RefreshedTokenError'))

                    resetSessionAndLogout()

                    return Promise.reject(new SilentCustomRequestError())
                }

                // If there is no error.response trigger an unknown error alert
                if (!error?.response) {
                    dispatch(receiveErrorAlerts(ERROR_MESSAGE_UNKNOWN))
                }

                // Note: For UK customer app this returns `error` rather than `error.response` – something to unify down the line
                // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                return Promise.reject(error?.response)
            }
        )
    }, [dispatch, resetSessionAndLogout])

    return <>{children}</>
}
